// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIALL

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Collapse, Divider, ListItemButton, Typography, Box, FormControlLabel, Checkbox, IconButton, Select, MenuItem, FormControl, SvgIcon } from '@mui/material';
import FeatureFilter from '@arcgis/core/layers/support/FeatureFilter';
import { useTheme } from '@mui/system';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { ChangeVersionAction, SetLayerVisibleAction } from 'Features/map/mapCommonActions';
import { ResetDrawSourceAction, SetlayerSelectedAction } from 'Features/map/mapEditorActions';
import { ToggleCadSiteVisibilityThunk, GetVectorTilesThunk } from 'Features/map/mapCommonThunk';
import { SetFileVersionThunk } from 'Features/order/orderThunk';
import SceneView from '@arcgis/core/views/SceneView';
import { forEach } from 'jszip';
import { orderStatusCode } from 'Utils/constants';
import CadDrawingsCheckbox from './CadDrawingsCheckbox';
import CadDrawingsButtons from './CadDrawingsButtons';
import ProjectLayersList from './ProjectLayersList';
import stylesDef from './styles';

interface ComponentProps {
  order: IOrder;
  index: number;
  openAdjustPanel?: (panelType: 'classified' | 'output' | 'input') => void;
  onAdjustPanelPositionChange?: (position: { top: number; left: number } | null) => void;
}

const CadDrawings: React.FC<ComponentProps> = ({ order, index, openAdjustPanel, onAdjustPanelPositionChange }) => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();

  const layers = useAppSelector((state) => state.map.common.vectorTileJson &&
    state.map.common.vectorTileJson[order._id] &&
    state.map.common.vectorTileJson[order._id][state.order.fileVersions[order._id]]);

  const { resources } = useAppSelector((state) => state.auth);
  const projectAccessUS = 'projectAccessUS' in resources;
  const projectAccessIndia = 'projectAccessIndia' in resources;
  const opsTrainer = 'opsTrainer' in resources;

  const orderId = order?._id;

  const [expand, setExpand] = useState(true);
  const [checked, setChecked] = useState(true);
  const [showAllLayers, setShowAllLayers] = useState(false);
  const [adjustButtonPosition, setAdjustButtonPosition] = useState<{ top: number; left: number } | null>(null);
  const adjustButtonRef = useRef<HTMLButtonElement | null>(null);
  const [has3DFeatures, setHas3DFeatures] = useState<boolean>(true);
  const lasViewRef = useRef<__esri.SceneView | null>(null);

  // TODO: use orderStatusCode constants instead of hardcoded values
  const hasNoOrder = !order;
  const orderAllowed = (projectAccessUS || projectAccessIndia || order.orderStatus === 0 || order.orderStatus === 7);
  const orderDisplayed = !hasNoOrder || orderAllowed;
  const isOrderCompleted = order.orderStatus === orderStatusCode.done;
  const toggleExpand = () => {
    setExpand(!expand);
  };

  const toggleChecked = () => {
    setChecked(!checked);
  };

  useEffect(() => {
    if (adjustButtonPosition && onAdjustPanelPositionChange) {
      onAdjustPanelPositionChange(adjustButtonPosition);
    }
  }, [adjustButtonPosition, onAdjustPanelPositionChange]);

  const handleAdjustPanelClick = (panelType: 'classified' | 'output' | 'input', event: React.MouseEvent<HTMLButtonElement>) => {
    const button = event.currentTarget;
    const rect = button.getBoundingClientRect();

    setAdjustButtonPosition({
      top: rect.bottom,
      left: rect.left,
    });

    openAdjustPanel?.(panelType);
  };

  // onfile change dropdown handler
  // Check if there are files in the cadFiles. If there's none present, display 'Original file;
  const files = order.cadFiles.map((cadFile) => {
    const { _id, s3Url, filename } = cadFile;
    const fileName = opsTrainer ? s3Url?.split('/').pop() : (filename?.split('/').pop());
    return { fileName, siteId: _id, visible: orderAllowed };
  });

  const visibleFiles = files.filter((f) => f.visible);

  const defaultFile = visibleFiles[visibleFiles.length - 1];
  const hasLayers = layers?.vector_layers && layers.vector_layers.length > 0;
  const [fileName, setFileName] = useState(defaultFile?.fileName || '');
  const [siteId, setSiteId] = useState(defaultFile?.siteId || '');

  // refer project layer list.ts and esrisplit viewer .tsx cascading files
  // Underground (MapImageLayer - 2D) state
  const [undergroundExpanded, setUndergroundExpanded] = useState(true);
  const [undergroundChecked, setUndergroundChecked] = useState(true);
  const [undergroundLayers, setUndergroundLayers] = useState<Record<string, boolean>>({});
  const [undergroundShowAll, setUndergroundShowAll] = useState(false);
  const [undergroundFileName, setUndergroundFileName] = useState(defaultFile?.fileName || '');
  const [undergroundSiteId, setUndergroundSiteId] = useState(defaultFile?.siteId || '');

  // Aerial (FeatureLayer - 3D) state
  const [aerialExpanded, setAerialExpanded] = useState(true);
  const [aerialChecked, setAerialChecked] = useState(true);
  const [aerialLayers, setAerialLayers] = useState<Record<string, boolean>>({});
  const [aerialShowAll, setAerialShowAll] = useState(false);
  const [aerialFileName, setAerialFileName] = useState(defaultFile?.fileName || '');
  const [aerialSiteId, setAerialSiteId] = useState(defaultFile?.siteId || '');
  const hasAerialLayers = hasLayers;

  const initializedRef = useRef(false);
  const lastOrderIdRef = useRef<string | null>(null);
  const lastSiteIdRef = useRef<string | null>(null);

  // Refs to prevent stale closure
  const undergroundLayersRef = useRef<Record<string, boolean>>({});
  const aerialLayersRef = useRef<Record<string, boolean>>({});
  const vectorLayersRef = useRef<any[]>([]);

  useEffect(() => {
    if (layers?.vector_layers) {
      vectorLayersRef.current = layers.vector_layers;
    }
  }, [layers?.vector_layers]);

  useEffect(() => {
    undergroundLayersRef.current = undergroundLayers;
  }, [undergroundLayers]);

  useEffect(() => {
    aerialLayersRef.current = aerialLayers;
  }, [aerialLayers]);

  useEffect(() => {
    const isValidSite = visibleFiles.some((f) => f.siteId === siteId);
    if (!isValidSite && visibleFiles.length > 0) {
      const newDefaultFile = visibleFiles[visibleFiles.length - 1];
      setSiteId(newDefaultFile.siteId);
      setFileName(newDefaultFile.fileName);
      setUndergroundSiteId(newDefaultFile.siteId);
      setUndergroundFileName(newDefaultFile.fileName);
      setAerialSiteId(newDefaultFile.siteId);
      setAerialFileName(newDefaultFile.fileName);
    }
  }, [order._id, visibleFiles, siteId]);

  // Initialize layer states when layers change
  useEffect(() => {
    if (!order) return;
    const orderChanged = lastOrderIdRef.current !== order._id;
    const siteChanged = lastSiteIdRef.current !== siteId;

    if (!layers?.vector_layers) return;

    if (initializedRef.current && !orderChanged && !siteChanged) return;

    const isValidSite = files.some((f) => f.siteId === siteId);
    if (!isValidSite) return;

    const initialState: Record<string, boolean> = {};
    layers.vector_layers.forEach((layer) => {
      initialState[layer.id] = true;
    });

    setUndergroundLayers({ ...initialState });
    setAerialLayers({ ...initialState });
    setUndergroundShowAll(false);
    setAerialShowAll(false);

    (window as any).undergroundCheckedItems = { ...initialState };
    (window as any).aerialCheckedItems = { ...initialState };

    undergroundLayersRef.current = { ...initialState };
    aerialLayersRef.current = { ...initialState };

    initializedRef.current = true;
    lastOrderIdRef.current = order._id;
    lastSiteIdRef.current = siteId;
  }, [layers?.vector_layers, order?._id, siteId, files]);

  // This effect runs when orderId and siteId become available (after order data loads).
  // It sets the file version in Redux, then fetches vector tiles, ensuring the 2D/3D
  // layer data is loaded even after a page reload.
  useEffect(() => {
    const initializeFileVersion = async () => {
      if (orderId && siteId) {
        dispatch(SetFileVersionThunk(orderId, siteId));
        await dispatch(GetVectorTilesThunk());
        dispatch(ChangeVersionAction());
      }
    };
    initializeFileVersion();
  }, [orderId, siteId, dispatch]);

  // Underground file change handler -  file change independently for aerial layer
  const onUndergroundFileChange = async (e: any) => {
    const site = e.target.value;
    const file = files.find((o) => o.siteId === site);
    if (!file) {
      return;
    }
    const { fileName } = file;
    setUndergroundFileName(fileName);
    setUndergroundSiteId(site);
    setSiteId(site);
    setFileName(fileName);

    const { zoomToSite } = (window as any);
    if (typeof zoomToSite === 'function') {
      zoomToSite(site);
    }
    dispatch(SetFileVersionThunk(orderId, site));
    dispatch(ResetDrawSourceAction());
    dispatch(SetlayerSelectedAction(null));
    await dispatch(GetVectorTilesThunk());

    if (checked && orderDisplayed) {
      await dispatch(
        ToggleCadSiteVisibilityThunk({
          orderId,
          siteId: site,
          visible: true,
        }),
      );
    }
    const layersArray = layers?.vector_layers;
    dispatch(ChangeVersionAction());
    dispatch(SetLayerVisibleAction(null));
    const visibleIds = Object.keys(undergroundLayersRef.current).filter((id) => undergroundLayersRef.current[id]);
    const expr = buildDefinitionExpression(visibleIds, site, layersArray);

    const { mapimagelayer } = (window as any);
    if (mapimagelayer) {
      mapimagelayer.visible = true;
      [0, 1, 2, 3].forEach((subId) => {
        const sublayer = mapimagelayer.findSublayerById(subId);
        if (sublayer) {
          sublayer.visible = true;
          sublayer.definitionExpression = expr;
        }
      });
    }
    if (order.orderStatus === orderStatusCode.done) {
      const { queryLayersTable } = (window as any);
      if (typeof queryLayersTable === 'function') {
        const has3D = await (window as any).checkfor3dfeature?.(site);
        queryLayersTable(site, has3D || false);
      }
    }
  };

  // Aerial file change handler - file change independently for aerial layer
  const onAerialFileChange = async (e: any) => {
    const site = e.target.value;
    const file = files.find((o) => o.siteId === site);
    if (!file) {
      return;
    }
    const { fileName } = file;
    setAerialFileName(fileName);
    setAerialSiteId(site);
    setSiteId(site);
    setFileName(fileName);

    const { zoomToSite } = (window as any);
    if (typeof zoomToSite === 'function') {
      zoomToSite(site);
    }
    dispatch(SetFileVersionThunk(orderId, site));
    dispatch(ResetDrawSourceAction());
    dispatch(SetlayerSelectedAction(null));
    await dispatch(GetVectorTilesThunk());

    if (checked && orderDisplayed) {
      await dispatch(
        ToggleCadSiteVisibilityThunk({
          orderId,
          siteId: site,
          visible: true,
        }),
      );
    }
    const layersArray = layers?.vector_layers;
    dispatch(ChangeVersionAction());
    dispatch(SetLayerVisibleAction(null));
    // Update only Aerial (3D FeatureLayers) with new siteId
    const visibleIds = Object.keys(aerialLayersRef.current).filter((id) => aerialLayersRef.current[id]);

    const { polylineLayer, polygonLayer, point3DLayer, text3Dlayer } = (window as any);
    const featureLayers = [polylineLayer, polygonLayer, point3DLayer, text3Dlayer].filter(Boolean);

    // Handle "all off" case
    if (visibleIds.length === 0) {
      featureLayers.forEach((layer: any) => {
        if (layer) {
          layer.visible = false;
          layer.featureEffect = null;
        }
      });
      return;
    }

    const layerIds = layersArray
      ?.filter((l) => visibleIds.includes(l.id))
      .map((l) => l.layerId) || [];

    const whereClause = layerIds.length > 0
      ? `site_id = '${site}' AND layer_id IN (${[...new Set(layerIds)].join(',')})`
      : `site_id = '${site}' AND 1=0`;

    const featureFilter = new FeatureFilter({
      where: whereClause
    });

    featureLayers.forEach((layer: any) => {
      if (layer) {
        layer.visible = true;
        layer.featureEffect = {
          filter: featureFilter,
          excludedEffect: 'opacity(0%)'
        };
      }
    });
  };

  const onFileChange = async (e: any) => {
    const site = e.target.value;
    const file = files.find((o) => o.siteId === site);
    if (!file) {
      return;
    }
    const { fileName } = file;
    setFileName(fileName);
    setSiteId(site);
    const { zoomToSite } = (window as any);
    if (typeof zoomToSite === 'function') {
      zoomToSite(site);
    }
    dispatch(SetFileVersionThunk(orderId, site));
    dispatch(ResetDrawSourceAction());
    dispatch(SetlayerSelectedAction(null));
    await dispatch(GetVectorTilesThunk());
    // Re-apply CAD rendering IF the panel is checked
    if (checked && orderDisplayed) {
      await dispatch(
        ToggleCadSiteVisibilityThunk({
          orderId,
          siteId: site,
          visible: true,
        }),
      );
    }
    dispatch(ChangeVersionAction());
    dispatch(SetLayerVisibleAction(null));
  };

  useEffect(() => {
    const togglecad = async () => {
      if (checked && orderDisplayed && siteId) {
        await dispatch(
          ToggleCadSiteVisibilityThunk({
            orderId,
            siteId,
            visible: true,
          }),
        );
      }
    };
    togglecad();
  }, [checked, orderDisplayed, siteId, dispatch, orderId]);

  // set definition expression for site filter and find the length of visible id's - refer esrisplitviewer.tsx
  const buildDefinitionExpression = (
    visibleLayerIds: string[],
    currentSiteId?: string,
    layersArray: LayerInfo[] = [],
  ): string => {
    if (!visibleLayerIds.length) {
      return '1=0';
    }

    const layerIds = layersArray
      .filter((l) => visibleLayerIds.includes(l.id))
      .map((l) => l.layerId);

    if (!layerIds.length) {
      return '1=0';
    }

    const siteClause = currentSiteId
      ? `site_id = '${currentSiteId}' AND `
      : '';

    return `${siteClause} layer_id IN (${[...new Set(layerIds)].join(',')})`;
  };

  // 2D - mapimage this function handles master toggle check box of underground layer
  // ======= UNDERGROUND HANDLERS =======
  const handleUndergroundMasterToggle = useCallback(() => {
    const newState = !undergroundChecked;
    setUndergroundChecked(newState);

    const newLayers: Record<string, boolean> = {};
    const newvisbilelayers : Record<string, boolean> = {};
    layers?.vector_layers.forEach((layer) => {
      newLayers[layer.id] = newState;
    });
    setUndergroundLayers(newLayers);
    (window as any).undergroundCheckedItems = { ...newLayers };

    const visibleIds = Object.keys(newLayers).filter((id) => newLayers[id]);
    const layersArray = layers?.vector_layers;
    const expr = buildDefinitionExpression(visibleIds, undergroundSiteId, layersArray);
    const { mapimagelayer } = (window as any);
    mapimagelayer.visible = true;
    if (mapimagelayer) {
      [0, 1, 2, 3].forEach((subId) => {
        const sublayer = mapimagelayer.findSublayerById(subId);
        if (sublayer) {
          sublayer.definitionExpression = expr;
          sublayer.visible = newState;
        }
      });
    }
  }, [undergroundChecked, layers, undergroundSiteId]);

  // 2D- mapimage this function handles eye layer toggle of underground layer
  const handleUndergroundLayerToggle = useCallback((layerId: string, forceState?: boolean) => {
    setUndergroundLayers((prev) => {
      const newState = forceState !== undefined ? forceState : !prev[layerId];
      const updated = { ...prev, [layerId]: newState };
      const layersArray = layers?.vector_layers;
      const visibleIds = Object.keys(updated).filter((id) => updated[id]);
      const expr = buildDefinitionExpression(visibleIds, undergroundSiteId, layersArray);

      const { mapimagelayer } = (window as any);
      if (mapimagelayer) {
        [0, 1, 2, 3].forEach((subId) => {
          const sublayer = mapimagelayer.findSublayerById(subId);
          if (sublayer) {
            sublayer.definitionExpression = expr;
          }
        });
      }
      const someOn = Object.values(updated).some((v) => v);
      setUndergroundChecked(someOn);

      return updated;
    });
  }, [undergroundSiteId]);

  // 3d - featurelayer this function handles master toggle check box of Aerial layer
  // ======= AERIAL HANDLERS =======
  const handleAerialMasterToggle = useCallback(() => {
    const newState = !aerialChecked;
    setAerialChecked(newState);

    const newLayers: Record<string, boolean> = {};
    layers?.vector_layers.forEach((layer) => {
      newLayers[layer.id] = newState;
    });

    setAerialLayers(newLayers);
    (window as any).aerialCheckedItems = { ...newLayers };
    const visibleIds = Object.keys(newLayers).filter((id) => newLayers[id]);

    const { polylineLayer, polygonLayer, point3DLayer, text3Dlayer } = (window as any);
    const featureLayers = [polylineLayer, polygonLayer, point3DLayer, text3Dlayer].filter(Boolean);

    // Handle "all off" case
    if (!newState || visibleIds.length === 0) {
      featureLayers.forEach((layer: any) => {
        if (layer) {
          layer.visible = false;
          layer.featureEffect = null;
        }
      });
      return;
    }

    const layersArray = layers?.vector_layers;

    const layerIds = layersArray
      .filter((l) => visibleIds.includes(l.id))
      .map((l) => l.layerId);

    const whereClause = layerIds.length > 0
      ? `site_id = '${aerialSiteId}' AND layer_id IN (${[...new Set(layerIds)].join(',')})`
      : `site_id = '${aerialSiteId}' AND 1=0`;

    const featureFilter = new FeatureFilter({
      where: whereClause
    });

    featureLayers.forEach((layer: any) => {
      if (layer) {
        layer.visible = true;
        layer.featureEffect = {
          filter: featureFilter,
          excludedEffect: 'opacity(0%)'
        };
      }
    });

  }, [aerialChecked, layers, aerialSiteId]);

  // 3d - featurelayer this function handles eye toggle of Aerial layer
  const handleAerialLayerToggle = useCallback((layerId: string, forceState?: boolean) => {
    setAerialLayers((prev) => {
      const newState = forceState !== undefined ? forceState : !prev[layerId];
      const updated = { ...prev, [layerId]: newState };
      aerialLayersRef.current = updated;
      (window as any).aerialCheckedItems = { ...updated };
      const visibleIds = Object.keys(updated).filter((id) => updated[id]);

      const { polylineLayer, polygonLayer, point3DLayer, text3Dlayer } = (window as any);
      const featureLayers = [polylineLayer, polygonLayer, point3DLayer, text3Dlayer].filter(Boolean);

      // Handle "all off" case
      if (visibleIds.length === 0) {
        featureLayers.forEach((layer: any) => {
          if (layer) {
            layer.visible = false;
            layer.featureEffect = null;
          }
        });
        setAerialChecked(false);
        return updated;
      }

      const layersArray = vectorLayersRef.current;

      const layerIds = layersArray
        ?.filter((l) => visibleIds.includes(l.id))
        .map((l) => l.layerId) || [];

      const whereClause = layerIds.length > 0
        ? `site_id = '${aerialSiteId}' AND layer_id IN (${[...new Set(layerIds)].join(',')})`
        : `site_id = '${aerialSiteId}' AND 1=0`;

      const featureFilter = new FeatureFilter({
        where: whereClause
      })

      featureLayers.forEach((layer: any) => {
        const lasView = (window as any).lasView;
        if (!lasView) {
          console.warn('lasView not ready yet');
          return;
        }
        lasView.whenLayerView(layer).then((layerView: any) => {
          layerView.filter = featureFilter;
        }).catch((err: any) => {
          console.error('whenLayerView failed:', err);
        });
      });

      setAerialChecked(Object.values(updated).some((v) => v));
      return updated;
    });
  }, [aerialSiteId]);

  useEffect(() => {
    setUndergroundShowAll(false);
    setAerialShowAll(false);
  }, [order?._id]);

  useEffect(() => {
    const check3DFeatures = async () => {
      if (!aerialSiteId) return;

      const { checkfor3dfeature } = (window as any);
      if (typeof checkfor3dfeature === 'function') {
        const has3D = await checkfor3dfeature(aerialSiteId);
        setHas3DFeatures(has3D);
      }
    };

    check3DFeatures();
  }, [aerialSiteId]);

  return (
    <>
          <Divider />
          {/* Underground Section (MapImageLayer - 2D)   */}
          <Box sx={styles.layerSection}>
            <ListItemButton
              sx={styles.expansionPanelHeader}
              onClick={() => setUndergroundExpanded(!undergroundExpanded)}
            >
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={!hasNoOrder && hasLayers && isOrderCompleted  && undergroundChecked}
                    onChange={handleUndergroundMasterToggle}
                    onClick={(e) => e.stopPropagation()}
                    color="primary"
                     disabled={hasNoOrder || !isOrderCompleted || !hasLayers || visibleFiles.length === 0}
                  />
                )}
                label={(
                  <Typography variant="h5" sx={styles.layerName}>
                    2D
                  </Typography>
                )}
                onClick={(e) => e.stopPropagation()}
              />
              <IconButton sx={styles.button}>
                {undergroundExpanded ? (
                  <KeyboardArrowDownIcon fontSize="small" />
                ) : (
                  <KeyboardArrowRightIcon fontSize="small" />
                )}
              </IconButton>
            </ListItemButton>

            <Collapse in={undergroundExpanded}>
              <Box sx={styles.wrapper}>
                {hasNoOrder ? (
                  <Typography variant="body2" sx={styles.fileItalic}>
                    No Order Placed
                  </Typography>
                ) : visibleFiles.length === 0 ? (
                  <Typography variant="body2" sx={styles.fileItalic}>
                    Data not available
                  </Typography>
                ) : !isOrderCompleted ? (
                  <Typography variant="body2" sx={styles.fileItalic}>
                    Data Coming Soon
                  </Typography>
                ) : !hasLayers ? (
                  <Typography variant="body2" sx={styles.fileItalic}>
                    Data Coming Soon
                  </Typography>
                ) : (
                  <>
                    <Select
                      value={undergroundSiteId}
                      onChange={onUndergroundFileChange}
                      displayEmpty
                      variant="standard"
                      disableUnderline
                      sx={styles.ongroundfilechange}
                      IconComponent={KeyboardArrowDownIcon}
                    >
                      {visibleFiles.map((file) => (
                        <MenuItem key={file.siteId} value={file.siteId}>
                          {file.fileName}
                        </MenuItem>
                      ))}
                    </Select>
                    <ProjectLayersList
                      order={order}
                      layers={layers}
                      siteId={siteId}
                      layerType="underground"
                      checkedLayers={undergroundLayers}
                      onToggleLayer={handleUndergroundLayerToggle}
                      limitTo={undergroundShowAll ? undefined : 5}
                      onRequestShowAll={() => setUndergroundShowAll(!undergroundShowAll)}
                    />
                  </>
                )}
              </Box>
            </Collapse>
          </Box>

          <Divider />

          {/* Aerial Section (FeatureLayer - 3D)   */}
          <Box sx={styles.layerSection}>
            <ListItemButton
              sx={styles.expansionPanelHeader}
              onClick={() => setAerialExpanded(!aerialExpanded)}
            >
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={!hasNoOrder && hasLayers && has3DFeatures && isOrderCompleted  && aerialChecked}
                    indeterminate={
                      !hasNoOrder &&
                      isOrderCompleted &&
                      !aerialChecked &&
                      Object.values(aerialLayers).some((v) => v)
                    }
                    onChange={handleAerialMasterToggle}
                    onClick={(e) => e.stopPropagation()}
                    color="primary"
                    disabled={hasNoOrder || !isOrderCompleted || !hasLayers || visibleFiles.length === 0}
                  />
                )}
                label={(
                  <Typography variant="h5" sx={styles.layerName}>
                    3D
                  </Typography>
                )}
                onClick={(e) => e.stopPropagation()}
              />
              {hasAerialLayers && has3DFeatures && isOrderCompleted && (
                <IconButton
                  ref={adjustButtonRef}
                  sx={styles.sliderbtn}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAdjustPanelClick('output', e);
                  }}
                >
                  <SvgIcon sx={styles.adjustpannelicon} viewBox="0 0 20 20">
                    <rect fill="#ffffff" width="20" height="20" />
                    <g transform="rotate(90 10 10)">
                      <path
                        d="M6.56176 10.625C6.56172 11.096 6.40962 11.5545 6.1281 11.9321C5.84658 12.3098 5.45067 12.5865 4.99926 12.7211V16.875C4.99926 17.0408 4.93341 17.1997 4.8162 17.3169C4.69899 17.4342 4.54002 17.5 4.37426 17.5C4.2085 17.5 4.04953 17.4342 3.93232 17.3169C3.81511 17.1997 3.74926 17.0408 3.74926 16.875V12.7211C3.29799 12.5864 2.90225 12.3096 2.62087 11.932C2.33949 11.5543 2.1875 11.0959 2.1875 10.625C2.1875 10.1541 2.33949 9.69568 2.62087 9.31803C2.90225 8.94038 3.29799 8.66362 3.74926 8.52891V3.125C3.74926 2.95924 3.81511 2.80027 3.93232 2.68306C4.04953 2.56585 4.2085 2.5 4.37426 2.5C4.54002 2.5 4.69899 2.56585 4.8162 2.68306C4.93341 2.80027 4.99926 2.95924 4.99926 3.125V8.52891C5.45067 8.66349 5.84658 8.94019 6.1281 9.31785C6.40962 9.69551 6.56172 10.154 6.56176 10.625ZM10.6243 4.77891V3.125C10.6243 2.95924 10.5584 2.80027 10.4412 2.68306C10.324 2.56585 10.165 2.5 9.99926 2.5C9.8335 2.5 9.67453 2.56585 9.55732 2.68306C9.44011 2.80027 9.37426 2.95924 9.37426 3.125V4.77891C8.92299 4.91362 8.52725 5.19038 8.24587 5.56803C7.96449 5.94568 7.8125 6.40405 7.8125 6.875C7.8125 7.34595 7.96449 7.80432 8.24587 8.18197C8.52725 8.55962 8.92299 8.83638 9.37426 8.97109V16.875C9.37426 17.0408 9.44011 17.1997 9.55732 17.3169C9.67453 17.4342 9.8335 17.5 9.99926 17.5C10.165 17.5 10.324 17.4342 10.4412 17.3169C10.5584 17.1997 10.6243 17.0408 10.6243 16.875V8.97109C11.0755 8.83638 11.4713 8.55962 11.7527 8.18197C12.034 7.80432 12.186 7.34595 12.186 6.875C12.186 6.40405 12.034 5.94568 11.7527 5.56803C11.4713 5.19038 11.0755 4.91362 10.6243 4.77891ZM16.2493 11.0289V3.125C16.2493 2.95924 16.1834 2.80027 16.0662 2.68306C15.949 2.56585 15.79 2.5 15.6243 2.5C15.4585 2.5 15.2995 2.56585 15.1823 2.68306C15.0651 2.80027 14.9993 2.95924 14.9993 3.125V11.0289C14.548 11.1636 14.1522 11.4404 13.8709 11.818C13.5895 12.1957 13.4375 12.6541 13.4375 13.125C13.4375 13.5959 13.5895 14.0543 13.8709 14.432C14.1522 14.8096 14.548 15.0864 14.9993 15.2211V16.875C14.9993 17.0408 15.0651 17.1997 15.1823 17.3169C15.2995 17.4342 15.4585 17.5 15.6243 17.5C15.79 17.5 15.949 17.4342 16.0662 17.3169C16.1834 17.1997 16.2493 17.0408 16.2493 16.875V15.2211C16.7005 15.0864 17.0963 14.8096 17.3777 14.432C17.659 14.0543 17.811 13.5959 17.811 13.125C17.811 12.6541 17.659 12.1957 17.3777 11.818C17.0963 11.4404 16.7005 11.1636 16.2493 11.0289Z"
                        fill="#788898"
                      />
                    </g>
                  </SvgIcon>
                </IconButton>
              )}
              <IconButton sx={styles.button}>
                {aerialExpanded ? (
                  <KeyboardArrowDownIcon fontSize="small" />
                ) : (
                  <KeyboardArrowRightIcon fontSize="small" />
                )}
              </IconButton>
            </ListItemButton>

            <Collapse in={aerialExpanded}>
              <Box sx={styles.wrapper}>
                {hasNoOrder ? (
                  <Typography variant="body2" sx={styles.fileItalic}>
                    No Order Placed
                  </Typography>
                ) : visibleFiles.length === 0 ? (
                  <Typography variant="body2" sx={styles.fileItalic}>
                    Data not available
                  </Typography>
                ) : !isOrderCompleted ? (
                  <Typography variant="body2" sx={styles.fileItalic}>
                    Data Coming Soon
                  </Typography>
                ) : !hasLayers ? (
                  <Typography variant="body2" sx={styles.fileItalic}>
                    Data Coming Soon
                  </Typography>
                ) : !has3DFeatures ? (
                  <Typography variant="body2" sx={styles.fileItalic}>
                    No Data Available
                  </Typography>
                ) : (
                  <>
                    <Select
                      value={aerialSiteId}
                      onChange={onAerialFileChange}
                      displayEmpty
                      variant="standard"
                      disableUnderline
                      sx={styles.onaerialfilechange}
                      IconComponent={KeyboardArrowDownIcon}
                    >
                      {visibleFiles.map((file) => (
                        <MenuItem key={file.siteId} value={file.siteId}>
                          {file.fileName}
                        </MenuItem>
                      ))}
                    </Select>
                    <ProjectLayersList
                      order={order}
                      layers={layers}
                      siteId={aerialSiteId}
                      layerType="aerial"
                      checkedLayers={aerialLayers}
                      onToggleLayer={handleAerialLayerToggle}
                      limitTo={aerialShowAll ? undefined : 5}
                      onRequestShowAll={() => setAerialShowAll(!aerialShowAll)}
                    />
                  </>
                )}
              </Box>
            </Collapse>
          </Box>

          <Divider />
        </>
  );
};

export default CadDrawings;
